// src/utils/brandHandoff.ts
import type { ResolutionKey } from "../config/plans";

export type DashboardStyle =
  | "Realista"
  | "Ilustración"
  | "Minimal"
  | "Futurista"
  | "Anime"
  | "Vintage"
  | "Logo corporativo"
  | "Portada épica";

/**
 * ✅ Extensión del handoff para organización tipo Canva:
 * - projectId: opcional (null/undefined = "Sin proyecto")
 * - campaignId: opcional (subnivel dentro del proyecto, típico Pro)
 *
 * Importante:
 * - Mantiene compatibilidad con payloads viejos (sin estos campos).
 */
export type BrandPendingPayload = {
  source: "brand";
  presetId: string;
  prompt: string;
  style: DashboardStyle;
  aspectRatio: string; // ej: "1:1", "4:5", "16:9"
  requestedResolution?: ResolutionKey; // sugerencia (el dashboard la ajusta si no está permitida)

  // ✅ NUEVO: organización opcional
  projectId?: string | null; // null/undefined => Sin proyecto
  campaignId?: string | null; // null/undefined => Sin campaña

  createdAt: number;
};

function keyForUser(userKey: string) {
  return `ovylon_brand_pending_${userKey}`;
}

/**
 * Normaliza IDs opcionales:
 * - "" -> undefined (no enviar)
 * - "none"/"null"/"undefined" -> null (equivalente a "sin X")
 * - null -> null
 * - otros strings -> string
 */
function normalizeMaybeId(v: unknown): string | null | undefined {
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return undefined;

    const low = s.toLowerCase();
    if (low === "none" || low === "null" || low === "undefined") return null;

    return s;
  }
  if (v === null) return null;
  return undefined;
}

function isDashboardStyle(v: unknown): v is DashboardStyle {
  return (
    v === "Realista" ||
    v === "Ilustración" ||
    v === "Minimal" ||
    v === "Futurista" ||
    v === "Anime" ||
    v === "Vintage" ||
    v === "Logo corporativo" ||
    v === "Portada épica"
  );
}

function safeParse(raw: string): BrandPendingPayload | null {
  try {
    const parsed = JSON.parse(raw) as Partial<BrandPendingPayload>;

    if (!parsed || parsed.source !== "brand") return null;
    if (!parsed.prompt || typeof parsed.prompt !== "string") return null;

    // ✅ Expiración simple: 6 horas
    const createdAt = typeof parsed.createdAt === "number" ? parsed.createdAt : 0;
    const MAX_AGE_MS = 6 * 60 * 60 * 1000;
    if (createdAt && Date.now() - createdAt > MAX_AGE_MS) return null;

    // ✅ Normalizar nuevos campos (compat con payload viejo)
    const projectId = normalizeMaybeId(parsed.projectId);
    const campaignId = normalizeMaybeId(parsed.campaignId);

    // ✅ Normalizar campos base con defaults seguros
    const presetId = typeof parsed.presetId === "string" ? parsed.presetId : String(parsed.presetId || "");
    const style: DashboardStyle = isDashboardStyle(parsed.style) ? parsed.style : "Realista";
    const aspectRatio =
      typeof parsed.aspectRatio === "string" && parsed.aspectRatio.trim() ? parsed.aspectRatio : "1:1";

    const requestedResolution =
      typeof parsed.requestedResolution === "string" ? (parsed.requestedResolution as ResolutionKey) : undefined;

    return {
      source: "brand",
      presetId,
      prompt: parsed.prompt,
      style,
      aspectRatio,
      requestedResolution,
      projectId,
      campaignId,
      createdAt: createdAt || Date.now(),
    };
  } catch {
    return null;
  }
}

/**
 * ✅ Guarda el payload para "handoff" hacia Dashboard
 */
export function setBrandPending(userKey: string, payload: BrandPendingPayload) {
  localStorage.setItem(keyForUser(userKey), JSON.stringify(payload));
}

/**
 * ✅ Lee sin borrar (compatibilidad)
 */
export function getBrandPending(userKey: string): BrandPendingPayload | null {
  const raw = localStorage.getItem(keyForUser(userKey));
  if (!raw) return null;
  return safeParse(raw);
}

/**
 * ✅ Borra manualmente (compatibilidad)
 */
export function clearBrandPending(userKey: string) {
  localStorage.removeItem(keyForUser(userKey));
}

/**
 * ✅ Lee Y borra (recomendado): consumir una sola vez
 */
export function consumeBrandPending(userKey: string): BrandPendingPayload | null {
  const k = keyForUser(userKey);
  const raw = localStorage.getItem(k);
  if (!raw) return null;

  const parsed = safeParse(raw);

  // ✅ siempre limpiamos para evitar loops/reintentos
  localStorage.removeItem(k);

  return parsed;
}

